import { renderRichText } from "@storyblok/react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { getStoryblokImageUrl } from "@/lib/image-utils";
import { getUrlFromMultilink } from "@/lib/utils";
import type { Copy as CopyBlok } from "@/types/storyblok";

export default function Copy({ blok }: { blok: CopyBlok }) {
  const imageUrl = blok.image
    ? getStoryblokImageUrl(blok.image, {
        width: 800,
        quality: 85,
        format: "webp",
      })
    : null;

  const subtitleLinkUrl = blok.subtitle_link
    ? getUrlFromMultilink(blok.subtitle_link)
    : null;
  const hasValidSubtitleLink =
    subtitleLinkUrl && subtitleLinkUrl !== "#" && subtitleLinkUrl.trim() !== "";

  // Determine aspect ratio class
  const getAspectRatioClass = () => {
    switch (blok.image_aspect_ratio) {
      case "square":
        return "aspect-square";
      case "landscape":
        return "aspect-video";
      case "portrait":
        return "aspect-[3/4]";
      default:
        return "aspect-[1/1]";
    }
  };

  const alignImageClass =
    blok.align_image === "left" ? "order-last md:order-first" : "md:order-last";

  const aspectRatioClass =
    blok.image_aspect_ratio === "logo" ? "" : getAspectRatioClass();

  const imageWrapperClassName =
    blok.image_aspect_ratio === "logo"
      ? `w-full ${alignImageClass} relative md:sticky md:top-32`
      : `w-full ${aspectRatioClass} ${alignImageClass} relative md:sticky md:top-32 bg-black shadow-2xl rounded-xl overflow-hidden`;

  // Render richtext content
  const renderedContent = blok.copy
    ? // @ts-expect-error - Storyblok richtext types are complex
      renderRichText(blok.copy)
    : null;

  return (
    <div
      className="relative"
      // biome-ignore lint/suspicious/noExplicitAny: Required for Storyblok React compatibility
      {...storyblokEditable(blok as any)}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 relative isolate pt-24 pb-48">
        <div className="flex flex-col space-y-4 max-w-screen-lg">
          {blok.subtitle &&
            (hasValidSubtitleLink ? (
              <Link
                href={subtitleLinkUrl}
                className="text-teal-500 flex items-center gap-2 text-2xl drop-shadow-2xl font-medium font-display uppercase hover:text-teal-600 transition-colors duration-300"
              >
                {blok.subtitle} <ArrowRight />
              </Link>
            ) : (
              <span className="text-teal-500 text-2xl drop-shadow-2xl font-medium font-display uppercase">
                {blok.subtitle}
              </span>
            ))}
          {blok.title && (
            <h2 className="text-5xl md:text-6xl drop-shadow-2xl font-extrabold font-display bg-gradient-to-br from-violet-100 via-zinc-50 to-violet-200 bg-clip-text text-transparent leading-tight pb-1">
              {blok.title}
            </h2>
          )}
          {blok.lead && (
            <p className="text-xl drop-shadow-2xl max-w-prose font-medium text-muted-foreground mt-4 sm:mt-6 mb-8">
              {blok.lead}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mt-12 relative isolate">
          {imageUrl && blok.image && (
            <div className={imageWrapperClassName}>
              {blok.image_aspect_ratio !== "logo" ? (
                <Image
                  src={imageUrl}
                  alt={blok.image.alt || blok.image.name || ""}
                  fill
                  className="w-full h-auto grayscale object-cover object-center"
                />
              ) : (
                <Image
                  src={imageUrl}
                  alt={blok.image.alt || blok.image.name || ""}
                  width={blok.image.width || 600}
                  height={blok.image.height || 600}
                  className="w-full h-auto brightness-0 invert transition-all duration-300"
                />
              )}
            </div>
          )}
          {renderedContent && (
            <div
              className="md:col-span-2 typography max-w-none"
              // biome-ignore lint: Required for Storyblok richtext rendering
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
